import { getTimeLeftInDay, HOURS, IDay, IState, offDutyStatuses } from "./helpers";
import { getWorkdayOffNeeded, shouldCarryWorkdayOffDuty } from "./hosCalculator";
import { getOffAndRestRequirementReductionForDeferralDay, getAdcExtensionAmountForDay } from "./exemptionHelpers";
import type { IHosRulesetConfig } from "../interfaces/hosRuleset";

export const getWorkdayRestAvailabilityForBetweenRestLimit = (state: IState, betweenRestLimit: number | undefined) => {
    let timeUntilMustRest: number | undefined;
    if (betweenRestLimit) {
        if (state.workday.endOfLastBreak === undefined) {
            timeUntilMustRest = betweenRestLimit;
        } else {
            let timeSinceLastBreak = state.nextDate.getTime() - state.workday.endOfLastBreak.getTime();
            timeUntilMustRest = betweenRestLimit - timeSinceLastBreak;
        }
    }
    return timeUntilMustRest;
};

export const getDailyDrivingLimit = (day: IDay, rulesetConfig: IHosRulesetConfig): undefined | number => {
    if (!day || rulesetConfig.daily?.driveMaximum === undefined) {
        return undefined;
    }
    let dailyDrivingLimit =
        rulesetConfig.daily.driveMaximum + getOffAndRestRequirementReductionForDeferralDay(day) + getAdcExtensionAmountForDay(day);
    if (day.deferralStatus === "DayTwo") {
        const twoDayMax = 2 * rulesetConfig.daily.driveMaximum;
        dailyDrivingLimit = Math.min(dailyDrivingLimit, twoDayMax - (day.yesterdayDriveCount ?? 0));
    }
    return dailyDrivingLimit;
};

export const getDailyDutyLimit = (day: IDay, rulesetConfig: IHosRulesetConfig): undefined | number => {
    if (!day || rulesetConfig.daily?.dutyMaximum === undefined) {
        return undefined;
    }
    return rulesetConfig.daily.dutyMaximum + getOffAndRestRequirementReductionForDeferralDay(day) + getAdcExtensionAmountForDay(day);
};

export const getDailyDrivingAvailabilityForDay = (day: IDay, config: IHosRulesetConfig): number | undefined => {
    let drivingLimit = getDailyDrivingLimit(day, config);
    return drivingLimit === undefined ? undefined : drivingLimit - day.driveCount;
};

export const getDailyDutyAvailabilityForDay = (day: IDay, config: IHosRulesetConfig): number | undefined => {
    let dailyDutyLimit = getDailyDutyLimit(day, config);
    return dailyDutyLimit === undefined ? undefined : dailyDutyLimit - day.onDutyCount;
};

// todo: check why this function is so complicated compared to getDailyDutyAvailabilityForDay
export const getDailyDutyAvailabilityForCurrentDay = (state: IState): number | undefined => {
    let hasNotGoneBackInTime = state.nextDateIndex === state.day.dateIndex;
    if (state.isInit && hasNotGoneBackInTime && state.config.daily?.dutyMaximum !== undefined) {
        let remainingDailyOffDutyRequired = getRemainingDailyOffDutyRequired(state.day, state.config, true);
        if (remainingDailyOffDutyRequired !== undefined) {
            // duty available will be limited by the time until the point of no return for the 10hr off duty requirement
            let timeLeftInDay = getTimeLeftInDay(state);
            let isCurrentlyInOff = offDutyStatuses.indexOf(state.current.status) > -1;
            let timeUntilMustRest = timeLeftInDay - remainingDailyOffDutyRequired;
            if (isCurrentlyInOff && state.day.unCountedOff) {
                // if currently in off, need to freeze the duty count down until the driver goes on-duty again
                timeUntilMustRest += state.day.unCountedOff;
            }
            let dailyDutyLeft = getDailyDutyAvailabilityForDay(state.day, state.config);
            return Math.min(dailyDutyLeft ?? timeUntilMustRest, timeUntilMustRest);
        }
        return getDailyDutyAvailabilityForDay(state.day, state.config);
    }
    return undefined;
};

export const getTotalDailyOffDutyRequired = (day: IDay, rulesetConfig: IHosRulesetConfig): undefined | number => {
    if (!day || rulesetConfig.daily?.offDutyMinimum === undefined) {
        return undefined;
    }
    return rulesetConfig.daily.offDutyMinimum - getOffAndRestRequirementReductionForDeferralDay(day) - getAdcExtensionAmountForDay(day);
};

export const getRemainingDailyOffDutyRequired = (day: IDay, rulesetConfig: IHosRulesetConfig, allowNegative = false): undefined | number => {
    let totalDailyOffDutyRequired = getTotalDailyOffDutyRequired(day, rulesetConfig);
    if (totalDailyOffDutyRequired === undefined) {
        return undefined;
    }
    let remainingDailyOffDutyRequired = totalDailyOffDutyRequired - day.offDutyCount;
    return allowNegative ? remainingDailyOffDutyRequired : Math.max(0, remainingDailyOffDutyRequired);
};

export const getDailyOffNeeded = (state: IState) => {
    const totalDailyOffDutyRequired = getTotalDailyOffDutyRequired(state.day, state.config);
    const remainingDailyOffDutyRequired = getRemainingDailyOffDutyRequired(state.day, state.config);
    if (totalDailyOffDutyRequired === undefined || remainingDailyOffDutyRequired === undefined) {
        return undefined;
    }
    if (shouldCarryWorkdayOffDuty(state)) {
        const workdayOffNeededBeforeEndOfDay = getWorkdayOffNeeded(state);
        if (workdayOffNeededBeforeEndOfDay > totalDailyOffDutyRequired || workdayOffNeededBeforeEndOfDay > state.day.offDutyCount) {
            return Math.max(remainingDailyOffDutyRequired, workdayOffNeededBeforeEndOfDay);
        }
    }
    return remainingDailyOffDutyRequired;
};

export const getTotalDailyRestRequired = (day: IDay, rulesetConfig: IHosRulesetConfig): undefined | number => {
    if (!day || rulesetConfig.rest?.dailyRestMinimum === undefined) {
        return undefined;
    }
    let result = rulesetConfig.rest.dailyRestMinimum - getAdcExtensionAmountForDay(day);
    if (day.deferralStatus === "DayOne" && day.deferralTime !== undefined) {
        result -= day.deferralTime;
    }
    return result;
};

export const getRemainingDailyRestRequired = (day: IDay, rulesetConfig: IHosRulesetConfig, allowNegative = false): undefined | number => {
    let totalDailyRestRequired = getTotalDailyRestRequired(day, rulesetConfig);
    if (totalDailyRestRequired === undefined) {
        return undefined;
    }
    let remainingRestRequired = totalDailyRestRequired - (day.restCount ?? 0);
    return allowNegative ? remainingRestRequired : Math.max(0, remainingRestRequired);
};

export const getDailyRestNeeded = (state: IState) => {
    let totalDailyRestRequired = getTotalDailyRestRequired(state.day, state.config);
    let remainingDailyRestRequired = getRemainingDailyRestRequired(state.day, state.config);
    if (totalDailyRestRequired === undefined || remainingDailyRestRequired === undefined) {
        return undefined;
    }
    return remainingDailyRestRequired;
};

export const getCyclePercentDrivingAvailability = (state: IState) => {
    let cycleConfig = state.config.cycle;
    if (!cycleConfig) {
        return undefined;
    }
    let maxPercentDriving = cycleConfig.maxPercentDriving;
    if (cycleConfig.cycleDays && maxPercentDriving) {
        // No more than 50% of your on-duty time can be spent driving in your cycle.
        let onDutyCount = (cycleConfig.cycleDays - state.cycle.days) * 24 * HOURS; // assume all hours are spent ON for empty cycle days.
        onDutyCount += state.cycle.onDutyCount;
        onDutyCount += getTimeLeftInDay(state); // assume time until end of day is spent in ON.
        // Math.trunc prevents fractional milliseconds when onDutyCount is an odd number of ms
        // (e.g. from sub-second log timestamps), which would produce a non-integer JSON value
        // that fails long deserialization in C#.
        let drivingMaximum = Math.trunc(0.5 * onDutyCount);
        return drivingMaximum - state.cycle.driveCount;
    }
    return undefined;
};

export const getCycleDrivingAvailability = (state: IState) => {
    let cycleDrivingMaximum = state.config.cycle?.cycleDrivingMaximum;
    if (!cycleDrivingMaximum) {
        return undefined;
    }
    return cycleDrivingMaximum - state.cycle.driveCount;
};


